import { StandardTabsV2 } from '@cimplico/components';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useIsFirmRole } from '../../../hooks/use-is-firm-role';
import { Entity } from '../../../types/entity';
import { useIsActiveSubscription } from '../../../hooks/use-is-active-subscription';
import FilesSection from '../../files/FilesSection';
import EntityInsights from './insights/EntityInsights';
import EntityIntegrations from './integrations/EntityIntegrations';
import WorkpapersSection from './workpapers/WorkpapersSection';
import EntityOverviewSettings from './settings/EntityOverviewSettings';
import ChartGrid from './chart-of-accounts/ChartGrid';

type EntityContentContainerProps = {
    entity: Entity;
    onXeroError: (error?: { name: string; message: string }) => void;
};

const tabs = ['insights', 'workpapers', 'files', 'charts', 'integrations', 'settings'];

function getSavedTab(isExternalUser = false, tab: string | null): number {
    if (!tab || (isExternalUser && (tab === 'integrations' || tab === 'settings'))) {
        return 0;
    }

    return tabs.includes(tab) ? tabs.indexOf(tab) : 0;
}

function EntityContentContainer({ entity, onXeroError }: EntityContentContainerProps): JSX.Element {
    const { isExternalUser } = useIsFirmRole();
    const [params, setParams] = useSearchParams();
    const [currentTab, setCurrentTab] = useState(getSavedTab(isExternalUser, params.get('tab')));
    const activeSubscription = useIsActiveSubscription();

    function handleTabChange(t: number): void {
        setCurrentTab(t);
        setParams({ tab: tabs[t] });
    }

    useEffect(() => {
        const newTab = getSavedTab(isExternalUser, params.get('tab'));

        if (newTab !== currentTab) {
            setCurrentTab(newTab);
        }
    }, [params.get('tab')]);

    return (
        <>
            <div className="border border-gray-200 dark:border-white/[0.07] rounded-lg w-full pb-4 grid gap-y-4 bg-white dark:bg-gray-800 relative">
                <div className="border-b dark:border-white/[0.07] px-8">
                    <StandardTabsV2 colorMode="light" onChange={handleTabChange} selectedIndex={currentTab} name="entity-overview-tab">
                        <StandardTabsV2.Tab label="Insights" />
                        <StandardTabsV2.Tab label="Workpapers" />
                        <StandardTabsV2.Tab label="Files" />
                        {!isExternalUser && <StandardTabsV2.Tab label="Chart of Accounts" />}
                        {!isExternalUser && <StandardTabsV2.Tab label="Integrations" />}
                        {!isExternalUser && <StandardTabsV2.Tab label="Settings" />}
                    </StandardTabsV2>
                </div>
                <div className="px-8 min-h-[400px]">
                    {isExternalUser ? (
                        <>
                            {currentTab === 0 && <EntityInsights />}
                            {currentTab === 1 && <WorkpapersSection entity={entity} />}
                            {currentTab === 2 && (
                                <FilesSection
                                    startingFolderId={entity.id}
                                    readOnly={!activeSubscription || entity.isArchived}
                                    entityId={entity.id}
                                />
                            )}
                        </>
                    ) : (
                        <>
                            {currentTab === 0 && <EntityInsights />}
                            {currentTab === 1 && <WorkpapersSection entity={entity} />}
                            {currentTab === 2 && (
                                <FilesSection
                                    startingFolderId={entity.id}
                                    readOnly={!activeSubscription || isExternalUser || entity.isArchived}
                                    entityId={entity.id}
                                />
                            )}

                            <>
                                {currentTab === 3 && <ChartGrid charts={entity.charts} entity={entity} />}
                                {currentTab === 4 && <EntityIntegrations entity={entity} onXeroError={onXeroError} />}
                                {currentTab === 5 && (
                                    <EntityOverviewSettings
                                        entity={entity}
                                        connectedToXero={Boolean(entity.integrations.xero)}
                                        onXeroError={onXeroError}
                                    />
                                )}
                            </>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

export default EntityContentContainer;
